// src/lib/api.js
// Thin fetch wrapper: attaches the access token, and on a single 401,
// tries the refresh flow once before giving up and forcing re-login.
// This mirrors the refresh-rotation contract from Module 2's routes/auth.js.

const BASE = '/api';

function getTokens() {
  return {
    accessToken: localStorage.getItem('sauti_access_token'),
    refreshToken: localStorage.getItem('sauti_refresh_token'),
  };
}

function setTokens({ accessToken, refreshToken }) {
  if (accessToken) localStorage.setItem('sauti_access_token', accessToken);
  if (refreshToken) localStorage.setItem('sauti_refresh_token', refreshToken);
}

export function clearTokens() {
  localStorage.removeItem('sauti_access_token');
  localStorage.removeItem('sauti_refresh_token');
}

let refreshPromise = null;

async function refresh() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { refreshToken } = getTokens();
      if (!refreshToken) throw new Error('No refresh token');
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) throw new Error('Refresh failed');
      const body = await res.json();
      setTokens(body);
      return body.accessToken;
    })().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export async function api(path, options = {}) {
  const { accessToken } = getTokens();
  const doFetch = (token) =>
    fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

  let res = await doFetch(accessToken);
  if (res.status === 401 && getTokens().refreshToken) {
    try {
      const newToken = await refresh();
      res = await doFetch(newToken);
    } catch {
      clearTokens();
      window.location.href = '/login';
      throw new Error('Session expired');
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : res.json();
}

export { setTokens, getTokens };
